import ICONS from "../../../../../assets/Icons";
import globalText from "../../../../../utils/globalText";
import useCategories from "../../../../../utils/useCategories";
import Loader from "../../../../../components/Loader/Index";
import PrimaryWithIconButton from "../../../../_utils/CommonButtons/PrimaryWithIconButton";
import SVGComponent from "../../../../_utils/SVGComponent";
import TickerAnimation from "../../../../_utils/TickerAnimation";

/**
 * BulkTransactionCategoryCard
 *
 * @param {Object} props
 * @param {"active"|"inactive"|"missing" | null} props.status  Which state to render.
 * @param {Object|null} [props.category]  The "Bulk Upload" category object (not needed when missing).
 * @param {string} [props.header]  Card heading.
 * @param {string} [props.notes]  Note shown under the active card.
 * @param {string} [props.activateText]  CTA label for the inactive state.
 * @param {Function} [props.onActivate]  Called when the inactive CTA is clicked.
 * @param {Function} [props.onCreate]  Called when "Create category" is clicked (missing state).
 * @param {boolean} [props.isCreateDisabled]  Disables the create button.
 * @param {boolean} [props.isLoading]
 */
const BulkTransactionCategoryCard = ({
  status,
  category = null,
  header,
  notes = "",
  activateText = "Activate Bulk Upload category",
  onActivate = () => {},
  onCreate = () => {},
  isCreateDisabled = false,
  isLoading = false,
}) => {
  const categoryMapper = useCategories();

  // Show the loader inside the card while categories are loading.
  if (isLoading) {
    return (
      <div className="w-width-full flex flex-col items-center justify-center gap-gap-sm rounded-border-radius-md bg-primary-card p-padding-md">
        <Loader
          width="var(--icon-lg)"
          height="var(--icon-lg)"
        />
      </div>
    );
  }

  if (!status) return null;

  if (status === "missing") {
    return (
      <div className="flex flex-col gap-gap-md p-padding-md rounded-border-radius-md bg-primary-card">
        <div className="flex items-center gap-gap-sm">
          <img
            src={ICONS?.ALERT}
            className="w-icon-md h-icon-md"
            alt="Warning"
          />
          <p className="text-text-primary text-font-md font-semibold">
            No ‘Bulk Upload’ category found
          </p>
        </div>

        <p className="text-text-secondary text-font-xs">
          To use bulk transaction upload, entity needs a dedicated category
          named <b>Bulk Upload</b>.
        </p>

        <div className="flex flex-col gap-gap-sm">
          <p className="text-text-primary text-font-sm font-semibold">
            To add the Bulk Upload category
          </p>

          <ul className="list-disc pl-padding-lg text-text-primary text-font-xs flex flex-col">
            <li>
              Add a category with the name <b>Bulk Upload</b>.
            </li>
            <li>
              Add 3 non-mandatory custom fields with the following names and
              type:
              <ul className="list-disc pl-padding-lg flex flex-col">
                <li>
                  <b>Custom field 1</b> - Input Field (Alphanumeric)
                </li>
                <li>
                  <b>Custom field 2</b> - Input Field (Alphanumeric)
                </li>
                <li>
                  <b>Custom field 3</b> - Input Field (Alphanumeric)
                </li>
              </ul>
            </li>
            <li>Make sure to set the category status to Active.</li>
            <li>
              Assign the category to every beneficiary account mentioned in the
              file for successful processing.
            </li>
          </ul>
        </div>

        <PrimaryWithIconButton
          btnText="Create category"
          btnType="button"
          icon={ICONS?.ADD_BUTTON_WHITE}
          iconToBeAlign="left"
          hoverOption="white"
          fullWidthRequired={false}
          inActiveVariantNeeded={false}
          enabledTextColor="var(--text-primary)"
          disabledTextColor="var(--text-primary)"
          enabledBgColor="var(--primary-nav-act)"
          imgColor="var(--text-primary)"
          isDisabled={isCreateDisabled}
          handleFunction={onCreate}
        />
      </div>
    );
  }

  if (!category) return null;

  const isInactive = status === "inactive";
  const description = category.description || "";
  const categoryIcon = categoryMapper(category.id);

  return (
    <div className="w-width-full">
      <div className="bg-primary-nav flex flex-col relative rounded-border-radius-md">
        <div className="bg-primary-card rounded-t-border-radius-md px-padding-md pt-padding-md">
          <p className="text-text-primary font-semibold text-base">
            {header?.trim() ? header : globalText.TRANSACTION_CATEGORY}
          </p>
        </div>

        <div className="bg-primary-card rounded-b-border-radius-md w-width-full">
          <div
            className={`w-width-full px-padding-md ${
              notes && !isInactive ? "" : "pb-padding-md"
            }`}
          >
            <p
              className={`text-text-primary text-sm font-semibold ${isInactive ? "py-padding-md" : "pt-padding-sm pb-padding-md"}`}
            >
              {isInactive && "Inactive category"}
            </p>

            <div
              className={`rounded-border-radius-md flex items-center relative z-z-index-base w-width-full ${
                isInactive ? "opacity-light-opacity pointer-events-none" : ""
              }`}
              style={{ background: "var(--category-background)" }}
            >
              <div className="bg-text-primary p-padding-sm flex items-center justify-center rounded-l-border-radius-md">
                <div className="w-icon-lg h-icon-lg flex-shrink-0">
                  <SVGComponent
                    url={categoryIcon?.url}
                    color={categoryIcon?.colour}
                  />
                </div>
              </div>

              <div
                className={`flex relative z-z-index-base rounded-r-border-radius-md flex-1 items-center overflow-hidden ${
                  isInactive ? "bg-primary-nav" : "bg-primary-nav-act"
                }`}
              >
                <div
                  className="absolute bg-positive-bg z-z-index-header"
                  style={{
                    transform: isInactive ? "scale(0)" : "scale(1)",
                    transformOrigin: "right",
                    transition: isInactive
                      ? "transform var(--animation-fast-xs) ease-in"
                      : "transform var(--animation-fast-s) ease-in",
                    height: "var(--height-full)",
                    width: "calc(var(--width-full) - var(--icon-lg))",
                  }}
                />
                <div
                  className="absolute bg-positive-bg z-z-index-header"
                  style={{
                    transform: isInactive ? "scale(0)" : "scale(1)",
                    transformOrigin: "left",
                    transition: isInactive
                      ? "transform var(--animation-fast-xs) ease-in"
                      : "transform var(--animation-fast-s) ease-in",
                    left: "calc(var(--left-full) - var(--icon-lg))",
                    height: "var(--height-full)",
                    width: "var(--icon-lg)",
                    borderRadius:
                      "var(--border-base) var(--border-radius-md) var(--border-radius-md) var(--border-base)",
                  }}
                />

                <div className="relative z-z-index-header flex flex-col justify-center flex-1 pr-padding-md pl-padding-sm py-padding-sm">
                  <p className="text-text-primary font-semibold text-sm break-all">
                    <TickerAnimation text={category.name} />
                  </p>
                  {description && (
                    <p
                      className={`text-xs mt-margin-xs break-all ${
                        isInactive ? "text-text-secondary" : "text-text-primary"
                      }`}
                    >
                      <TickerAnimation text={description} />
                    </p>
                  )}
                </div>

                {!isInactive && (
                  <div className="pr-padding-md py-padding-md relative z-z-index-header">
                    <img
                      className="w-icon-md h-icon-md"
                      src={ICONS.RADIO_BUTTON_SELECTED}
                      alt="selected"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isInactive && notes && (
            <p className="text-text-primary text-font-xs p-padding-md">
              {notes}
            </p>
          )}
        </div>

        {isInactive && (
          <div className="flex flex-col gap-gap-md">
            <div className="rounded-border-radius-md p-padding-sm">
              <div
                role="button"
                tabIndex={0}
                className="flex items-center cursor-pointer global-hover-white rounded-border-radius-md p-padding-sm relative z-z-index-base"
                onClick={onActivate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onActivate();
                  }
                }}
              >
                <span className="text-text-primary text-font-sm">
                  {activateText}
                </span>

                <div className="ml-auto">
                  <SVGComponent
                    url={ICONS?.ARROW_RIGHT_WHITE}
                    color="var(--text-primary)"
                    height="var(--icon-sm)"
                    width="var(--icon-sm)"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTransactionCategoryCard;
